// THE WEBSITE'S ACCOUNT EMAILS, OVER THE TRANSPORT (ACC-1): the real `AccountEmails` the sign-in seam asks for
// (services/auth.ts, ACC-2). Each method is one v2Credits op — `sendVerification`, `sendPasswordReset`, `sendEmailChange`,
// `sendPasswordChanged` (contract 2.16.0) — and the answer is turned into the seam's typed result. No sentence here:
// the codes map to ACC-3a's approved copy (C3, C5, D2 and the rest), exactly as the seam's other refusals do.
//
// THE SERVER DECIDES EVERYTHING. Who may ask, which address is sent what, the limits, and that a reset answers the same
// whether or not an account uses the address (D2) are `functions/src/v2/account/ops.ts`'s. This file only reads codes:
//   ACCOUNT_REQUIRED  → notSignedIn        (the server would not send a look-around's own-account email)
//   RATE_LIMITED      → tooManyTries       (C3)
//   BAD_REQUEST with `account.email.notAnEmail` → notAnEmail (C5)
//   BAD_REQUEST otherwise, UNAVAILABLE, INTERNAL → emailsUnavailable (switched off, or cannot send right now)
//   no connection     → network            (C9)
//   anything else     → unknown, with what arrived — an alarm, as the seam's own `unknown` is.

import type { CreditsAccountEmailResponse, V2Result } from "@contract";
import type { Transport } from "../transport/types";
import type { AccountEmails, OpResult } from "./auth";

/** The copy code the server sends with C5, the one BAD_REQUEST that is the owner's to fix. */
const NOT_AN_EMAIL_COPY = "account.email.notAnEmail";

export function accountEmailResult(result: V2Result<CreditsAccountEmailResponse>): OpResult {
  if (result.ok) return { ok: true };
  if (result.kind === "network") return { ok: false, refusal: { kind: "network" } };
  if (result.kind !== "v2Error") return { ok: false, refusal: { kind: "unknown", code: result.kind } };
  const error = result.error.error;
  switch (error.code) {
    case "ACCOUNT_REQUIRED":
      return { ok: false, refusal: { kind: "notSignedIn" } };
    case "RATE_LIMITED":
      return { ok: false, refusal: { kind: "tooManyTries" } };
    case "BAD_REQUEST":
      return error.copy.code === NOT_AN_EMAIL_COPY
        ? { ok: false, refusal: { kind: "notAnEmail" } }
        : { ok: false, refusal: { kind: "emailsUnavailable" } };
    case "UNAVAILABLE":
    case "INTERNAL":
      return { ok: false, refusal: { kind: "emailsUnavailable" } };
    default:
      return { ok: false, refusal: { kind: "unknown", code: error.code } };
  }
}

export function createTransportAccountEmails(transport: Transport): AccountEmails {
  return {
    sendVerification: async () => accountEmailResult(await transport.v2Credits.sendVerification()),
    sendPasswordReset: async (email) => accountEmailResult(await transport.v2Credits.sendPasswordReset({ email })),
    sendEmailChange: async (newEmail) => accountEmailResult(await transport.v2Credits.sendEmailChange({ newEmail })),
    sendPasswordChanged: async () => accountEmailResult(await transport.v2Credits.sendPasswordChanged()),
  };
}
